import React from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Button,
  Input,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  HStack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { BsTrash, BsPlus } from "react-icons/bs";

const LineItemsTable = ({ lineItems, setLineItems, readOnly = false }) => {
  const addLineItem = () => {
    setLineItems([
      ...lineItems,
      {
        id: Date.now(),
        itemName: "",
        description: "",
        quantity: 1,
        unitPrice: 0,
        unit: "pcs",
        totalPrice: 0,
      },
    ]);
  };

  const updateLineItem = (id, field, value) => {
    setLineItems(
      lineItems.map((item) => {
        if (item.id === id || item._id === id) {
          const updated = { ...item, [field]: value };
          // Auto-calculate total price
          if (field === "quantity" || field === "unitPrice") {
            updated.totalPrice =
              (updated.quantity || 0) * (updated.unitPrice || 0);
          }
          return updated;
        }
        return item;
      }),
    );
  };

  const removeLineItem = (id) => {
    setLineItems(lineItems.filter((item) => item.id !== id && item._id !== id));
  };

  const calculateGrandTotal = () => {
    return lineItems.reduce((sum, item) => {
      const total = (item.quantity || 0) * (item.unitPrice || 0);
      return sum + total;
    }, 0);
  };

  if (readOnly) {
    return (
      <Box>
        <Box overflowX="auto">
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>Item Name</Th>
                <Th>Description</Th>
                <Th isNumeric>Quantity</Th>
                <Th>Unit</Th>
                <Th isNumeric>Unit Price</Th>
                <Th isNumeric>Total</Th>
              </Tr>
            </Thead>
            <Tbody>
              {lineItems.map((item, index) => (
                <Tr key={item._id || item.id || index}>
                  <Td>{item.itemName}</Td>
                  <Td>{item.description}</Td>
                  <Td isNumeric>{item.quantity}</Td>
                  <Td>{item.unit || "pcs"}</Td>
                  <Td isNumeric>₹{item.unitPrice?.toFixed(2)}</Td>
                  <Td isNumeric fontWeight="semibold">
                    ₹{((item.quantity || 0) * (item.unitPrice || 0)).toFixed(2)}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
        <Box mt={4} textAlign="right">
          <Text fontSize={{ base: "md", md: "lg" }} fontWeight="bold">
            Grand Total: ₹{calculateGrandTotal().toFixed(2)}
          </Text>
        </Box>
      </Box>
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      <Box overflowX="auto">
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th>Item Name *</Th>
              <Th>Description</Th>
              <Th isNumeric>Qty *</Th>
              <Th>Unit</Th>
              <Th isNumeric>Unit Price *</Th>
              <Th isNumeric>Total</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {lineItems.map((item, index) => (
              <Tr key={item._id || item.id || index}>
                <Td>
                  <Input
                    size="sm"
                    value={item.itemName}
                    onChange={(e) =>
                      updateLineItem(
                        item._id || item.id,
                        "itemName",
                        e.target.value,
                      )
                    }
                    placeholder="Item name"
                  />
                </Td>
                <Td>
                  <Input
                    size="sm"
                    value={item.description}
                    onChange={(e) =>
                      updateLineItem(
                        item._id || item.id,
                        "description",
                        e.target.value,
                      )
                    }
                    placeholder="Description"
                  />
                </Td>
                <Td>
                  <NumberInput
                    size="sm"
                    min={1}
                    step={1}
                    clampValueOnBlur
                    value={item.quantity}
                    onChange={(valueString) =>
                      updateLineItem(
                        item._id || item.id,
                        "quantity",
                        parseInt(valueString) || 1,
                      )
                    }
                    maxW="120px"
                  >
                    <NumberInputField
                      textAlign="center"
                      inputMode="numeric"
                      aria-label="Quantity (use up and down arrows)"
                    />
                    <NumberInputStepper>
                      <NumberIncrementStepper aria-label="Increase quantity" />
                      <NumberDecrementStepper aria-label="Decrease quantity" />
                    </NumberInputStepper>
                  </NumberInput>
                  {/* <Text fontSize="xs" color="gray.500" mt={1}>
                    Use +/- arrows
                  </Text> */}
                </Td>
                <Td>
                  <Input
                    size="sm"
                    value={item.unit}
                    onChange={(e) =>
                      updateLineItem(
                        item._id || item.id,
                        "unit",
                        e.target.value,
                      )
                    }
                    placeholder="Unit"
                    maxW="80px"
                  />
                </Td>
                <Td>
                  <NumberInput
                    size="sm"
                    min={0}
                    step={0.01}
                    precision={2}
                    clampValueOnBlur
                    value={item.unitPrice}
                    onChange={(valueString) =>
                      updateLineItem(
                        item._id || item.id,
                        "unitPrice",
                        parseFloat(valueString) || 0,
                      )
                    }
                    maxW="120px"
                  >
                    <NumberInputField
                      textAlign="center"
                      inputMode="decimal"
                      aria-label="Unit price (use up and down arrows)"
                    />
                    <NumberInputStepper>
                      <NumberIncrementStepper aria-label="Increase unit price" />
                      <NumberDecrementStepper aria-label="Decrease unit price" />
                    </NumberInputStepper>
                  </NumberInput>
                  {/* <Text fontSize="xs" color="gray.500" mt={1}>
                    Use +/- arrows
                  </Text> */}
                </Td>
                <Td isNumeric fontWeight="semibold">
                  ₹{item.totalPrice?.toFixed(2) || "0.00"}
                </Td>
                <Td>
                  <IconButton
                    size="sm"
                    icon={<BsTrash />}
                    colorScheme="red"
                    variant="ghost"
                    onClick={() => removeLineItem(item._id || item.id)}
                    aria-label="Remove item"
                  />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      <HStack justify="space-between" flexWrap="wrap" gap={2}>
        <Button
          leftIcon={<BsPlus />}
          onClick={addLineItem}
          size={{ base: "sm", md: "sm" }}
          variant="outline"
          colorScheme="blue"
        >
          Add Line Item
        </Button>
        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="bold">
          Grand Total: ₹{calculateGrandTotal().toFixed(2)}
        </Text>
      </HStack>
    </VStack>
  );
};

export default LineItemsTable;
